<template>
	<Teleport to="body">
		<Transition name="modal-fade">
			<div
				v-if="isVisible"
				class="permission-modal-overlay"
				@click.self="close"
			>
				<div class="permission-modal">
					<!-- Декоративная линия сверху -->
					<div class="permission-modal__accent-line"></div>
					
					<div class="permission-modal__header">
						<div class="permission-modal__icon-wrapper">
							<div class="permission-modal__icon">
								<svg
									width="32"
									height="32"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								>
									<circle cx="12" cy="12" r="10" />
									<line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
								</svg>
							</div>
							<!-- Пульсирующий круг -->
							<div class="permission-modal__pulse"></div>
						</div>
						<h3 class="permission-modal__title">Доступ запрещён</h3>
					</div>

					<div class="permission-modal__body">
						<p class="permission-modal__message">
							{{ message || 'У вас недостаточно прав для выполнения этого действия.' }}
						</p>
						<div class="permission-modal__divider"></div>
						<p class="permission-modal__hint">
							<svg 
								width="16" 
								height="16" 
								viewBox="0 0 24 24" 
								fill="none" 
								stroke="currentColor" 
								stroke-width="2"
							>
								<circle cx="12" cy="12" r="10"/>
								<path d="M12 16v-4"/>
								<path d="M12 8h.01"/>
							</svg>
							Обратитесь к администратору для получения необходимых разрешений.
						</p>
					</div>

					<div class="permission-modal__footer">
						<button
							type="button"
							class="permission-modal__btn"
							@click="close"
						>
							<span>Понятно</span>
							<svg 
								width="18" 
								height="18" 
								viewBox="0 0 24 24" 
								fill="none" 
								stroke="currentColor" 
								stroke-width="2"
							>
								<polyline points="9 18 15 12 9 6"/>
							</svg>
						</button>
					</div>
				</div>
			</div>
		</Transition>
	</Teleport>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const isVisible = ref(false)
const message = ref('')

// Обработчик события 403
const handlePermissionDenied = (event) => {
	message.value = event.detail?.message || ''
	isVisible.value = true
}

const close = () => {
	isVisible.value = false
	message.value = ''
}

// Закрытие по Escape
const handleKeydown = (e) => {
	if (e.key === 'Escape' && isVisible.value) {
		close()
	}
}

onMounted(() => {
	window.addEventListener('permission:denied', handlePermissionDenied)
	window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
	window.removeEventListener('permission:denied', handlePermissionDenied)
	window.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
/* Подключение шрифтов */
@import url('https://fonts.googleapis.com/css2?family=Bitter:wght@400;500;600&family=Montserrat:wght@300;400;500&display=swap');

/* CSS переменные для корпоративных цветов */
:root {
	--corp-white: #ffffff;
	--corp-blue: #0096ed;
	--corp-gray: #636363;
	--corp-danger: #dc3545;
	--corp-danger-dark: #c82333;
}

/* Overlay */
.permission-modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(99, 99, 99, 0.6);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 10001;
	backdrop-filter: blur(6px);
	-webkit-backdrop-filter: blur(6px);
}

/* Modal */
.permission-modal {
	background: #ffffff;
	border-radius: 12px;
	width: 100%;
	max-width: 440px;
	box-shadow: 
		0 25px 50px -12px rgba(0, 0, 0, 0.25),
		0 0 0 1px rgba(0, 150, 237, 0.1);
	overflow: hidden;
	animation: modal-entrance 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
	position: relative;
}

@keyframes modal-entrance {
	0% {
		transform: scale(0.8) translateY(20px);
		opacity: 0;
	}
	100% {
		transform: scale(1) translateY(0);
		opacity: 1;
	}
}

/* Акцентная линия сверху */
.permission-modal__accent-line {
	height: 4px;
	background: linear-gradient(90deg, #dc3545 0%, #0096ed 100%);
	animation: line-shimmer 2s ease-in-out infinite;
}

@keyframes line-shimmer {
	0%, 100% {
		background: linear-gradient(90deg, #dc3545 0%, #0096ed 100%);
	}
	50% {
		background: linear-gradient(90deg, #0096ed 0%, #dc3545 100%);
	}
}

/* Header */
.permission-modal__header {
	padding: 32px 24px 20px;
	text-align: center;
	background: #ffffff;
}

.permission-modal__icon-wrapper {
	position: relative;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 16px;
}

.permission-modal__icon {
	width: 64px;
	height: 64px;
	border-radius: 50%;
	background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	color: #ffffff;
	animation: icon-shake 0.6s ease-in-out;
	animation-delay: 0.3s;
	position: relative;
	z-index: 2;
}

@keyframes icon-shake {
	0%, 100% { transform: rotate(0deg); }
	20% { transform: rotate(-8deg); }
	40% { transform: rotate(8deg); }
	60% { transform: rotate(-5deg); }
	80% { transform: rotate(5deg); }
}

.permission-modal__pulse {
	position: absolute;
	width: 64px;
	height: 64px;
	border-radius: 50%;
	background: #dc3545;
	animation: pulse-ring 1.5s ease-out infinite;
	z-index: 1;
}

@keyframes pulse-ring {
	0% {
		transform: scale(1);
		opacity: 0.4;
	}
	100% {
		transform: scale(1.8);
		opacity: 0;
	}
}

.permission-modal__title {
	margin: 0;
	font-family: 'Bitter', serif;
	font-size: 24px;
	font-weight: 600;
	color: #dc3545;
	letter-spacing: -0.02em;
	animation: title-fade-in 0.5s ease-out;
	animation-delay: 0.2s;
	animation-fill-mode: both;
}

@keyframes title-fade-in {
	from {
		opacity: 0;
		transform: translateY(10px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

/* Body */
.permission-modal__body {
	padding: 0 24px 24px;
	text-align: center;
	animation: body-slide-up 0.5s ease-out;
	animation-delay: 0.3s;
	animation-fill-mode: both;
}

@keyframes body-slide-up {
	from {
		opacity: 0;
		transform: translateY(15px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

.permission-modal__message {
	margin: 0;
	font-family: 'Montserrat', sans-serif;
	font-weight: 400;
	font-size: 15px;
	color: #636363;
	line-height: 1.6;
}

.permission-modal__divider {
	width: 60px;
	height: 2px;
	background: linear-gradient(90deg, transparent, #0096ed, transparent);
	margin: 20px auto;
	border-radius: 1px;
}

.permission-modal__hint {
	margin: 0;
	font-family: 'Montserrat', sans-serif;
	font-weight: 300;
	font-size: 13px;
	color: #636363;
	opacity: 0.85;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
}

.permission-modal__hint svg {
	color: #0096ed;
	flex-shrink: 0;
}

/* Footer */
.permission-modal__footer {
	padding: 0 24px 28px;
	display: flex;
	justify-content: center;
	animation: footer-fade-in 0.5s ease-out;
	animation-delay: 0.4s;
	animation-fill-mode: both;
}

@keyframes footer-fade-in {
	from {
		opacity: 0;
	}
	to {
		opacity: 1;
	}
}

.permission-modal__btn {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 14px 36px;
	background: linear-gradient(135deg, #0096ed 0%, #007acc 100%);
	color: #ffffff;
	border: none;
	border-radius: 8px;
	font-family: 'Montserrat', sans-serif;
	font-size: 15px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	position: relative;
	overflow: hidden;
	box-shadow: 0 4px 15px rgba(0, 150, 237, 0.3);
}

.permission-modal__btn::before {
	content: '';
	position: absolute;
	top: 0;
	left: -100%;
	width: 100%;
	height: 100%;
	background: linear-gradient(
		90deg,
		transparent,
		rgba(255, 255, 255, 0.2),
		transparent
	);
	transition: left 0.5s ease;
}

.permission-modal__btn:hover {
	transform: translateY(-3px);
	box-shadow: 0 8px 25px rgba(0, 150, 237, 0.4);
	background: linear-gradient(135deg, #00a8ff 0%, #0096ed 100%);
}

.permission-modal__btn:hover::before {
	left: 100%;
}

.permission-modal__btn:hover svg {
	transform: translateX(4px);
}

.permission-modal__btn svg {
	transition: transform 0.3s ease;
}

.permission-modal__btn:active {
	transform: translateY(-1px);
	box-shadow: 0 4px 12px rgba(0, 150, 237, 0.3);
}

.permission-modal__btn:focus {
	outline: none;
	box-shadow: 
		0 4px 15px rgba(0, 150, 237, 0.3),
		0 0 0 3px rgba(0, 150, 237, 0.2);
}

/* Transition */
.modal-fade-enter-active {
	transition: opacity 0.3s ease;
}

.modal-fade-leave-active {
	transition: opacity 0.25s ease;
}

.modal-fade-leave-active .permission-modal {
	animation: modal-exit 0.25s ease-in forwards;
}

@keyframes modal-exit {
	to {
		transform: scale(0.9) translateY(10px);
		opacity: 0;
	}
}

.modal-fade-enter-from,
.modal-fade-leave-to {
	opacity: 0;
}

/* Accessibility */
@media (prefers-reduced-motion: reduce) {
	.permission-modal {
		animation: none;
	}
	
	.permission-modal__icon {
		animation: none;
	}
	
	.permission-modal__pulse {
		animation: none;
		display: none;
	}
	
	.permission-modal__accent-line {
		animation: none;
	}
	
	.permission-modal__title,
	.permission-modal__body,
	.permission-modal__footer {
		animation: none;
	}
	
	.modal-fade-enter-active,
	.modal-fade-leave-active {
		transition: none;
	}
	
	.modal-fade-leave-active .permission-modal {
		animation: none;
	}
	
	.permission-modal__btn {
		transition: none;
	}
	
	.permission-modal__btn::before {
		display: none;
	}
}

/* Mobile responsive */
@media (max-width: 480px) {
	.permission-modal {
		max-width: calc(100% - 32px);
		margin: 16px;
		border-radius: 10px;
	}
	
	.permission-modal__header {
		padding: 24px 20px 16px;
	}
	
	.permission-modal__title {
		font-size: 20px;
	}
	
	.permission-modal__body {
		padding: 0 20px 20px;
	}
	
	.permission-modal__message {
		font-size: 14px;
	}
	
	.permission-modal__footer {
		padding: 0 20px 24px;
	}
	
	.permission-modal__btn {
		width: 100%;
		justify-content: center;
		padding: 14px 24px;
	}
}
</style>